import DOMPurify from "dompurify";

/**
 * Central HTML sanitizer for anything rendered with `v-html`.
 *
 * All user-generated content in this app (report/deadline descriptions,
 * comments, day-report lines, mentions, notification text, account names) is
 * authored in the rich-text editor and stored as HTML, then rendered with
 * `v-html`. Without sanitization that is a stored-XSS sink: a crafted report or
 * comment would execute script in every colleague's browser and, because the
 * JWT lives in localStorage, allow account takeover across the tenant.
 *
 * The allow-list below is intentionally limited to the tags/attributes the
 * TipTap editor actually emits. Notably:
 *  - `ALLOW_DATA_ATTR` keeps the `data-mention-*` attributes that drive the
 *    contact/user mention popups and the `data-full_text`/`data-short_text`
 *    attributes used by the blockquote "expand" behaviour.
 *  - all inline event handlers (`onclick`, `onerror`, …), `<script>`,
 *    `<style>`, and `javascript:` URLs are stripped by DOMPurify.
 *
 * Note: sanitizing here is defence-in-depth on the render side. The backend
 * also sanitizes the same fields on write, so a value can never reach storage
 * dirty in the first place.
 */

const ALLOWED_TAGS = [
  "p",
  "br",
  "span",
  "div",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "del",
  "mark",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "code",
  "pre",
  "hr",
  "button",
];

const ALLOWED_ATTR = ["class", "href", "target", "rel", "title"];

// Force any link that opens in a new tab to also drop the opener reference,
// closing the reverse-tabnabbing hole. DOMPurify already strips `javascript:`
// and other dangerous URI schemes from `href`.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Sanitize an HTML string for safe use with `v-html`.
 * @param {string | null | undefined} dirty raw HTML
 * @returns {string} sanitized HTML (empty string for nullish input)
 */
export function sanitizeHtml(dirty) {
  if (dirty === null || dirty === undefined) return "";

  return DOMPurify.sanitize(String(dirty), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
  });
}

export default sanitizeHtml;
